import { Dictionary } from 'lodash';
import React, { useMemo } from 'react';
import { User } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { SchedulerResourceType } from 'components/organisms/resource-management/resource-types';
import { RoleForProjectView } from 'components/organisms/resource-management/scheduler-config/columns/cells/role-for-projects-view';
import { ColumnConfig } from 'components/organisms/resource-management/scheduler-config/columns/types';
import { SchedulerReference } from 'components/organisms/resource-management/types';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useTranslation } from 'hooks/use-translation';
import { TemplateAssignee } from '../../types';
import {
  AssignUserOrTeam,
  TeamCell,
  TemplateAllocation,
  TitleCell,
  UserCell,
} from './cells';

interface Props {
  schedulerRef: SchedulerReference;
  userLookup: Dictionary<User>;
  allTasksAssignees: TemplateAssignee;
  onAddUsers: () => void;
  onAddTeams: () => void;
  onTeamEdit: (teamId: number) => void;
}
export const useColumnsConfig = ({
  schedulerRef,
  onAddUsers,
  onAddTeams,
  onTeamEdit,
  userLookup,
  allTasksAssignees,
}: Props): ColumnConfig[] => {
  const projectColumnTitle = useTranslation('resources');
  const allocationColumnTitle = useTranslation(
    'resource_management.columns.allocation',
  );
  const totalAllocationTitle = useTranslation('total_allocation');
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );

  return useMemo(
    () => [
      {
        text: projectColumnTitle,
        width: 360,
        type: 'tree',
        id: 'nameColumn',
        renderer: (arg) => {
          const { record, size, cellElement } = arg;
          const { type, autoAllocation } = record;

          record.cls = `${type}${
            autoAllocation === true ? ' auto-allocate' : ''
          }`;

          switch (type as SchedulerResourceType) {
            case SchedulerResourceType.Role:
              size.height = 32;
              cellElement.classList.add('full-width');

              return (
                <RoleForProjectView
                  onWrapperClick={() => {
                    schedulerRef.current.instance.toggleCollapse(record.id);
                  }}
                  roleName={record.roleName}
                />
              );
            case SchedulerResourceType.Project:
              size.height = 48;
              cellElement.classList.add('non-clickable');

              return <TitleCell title={totalAllocationTitle} />;
            case SchedulerResourceType.User:
              return (
                <UserCell
                  taskAssignedUserIds={allTasksAssignees.users.map(
                    (x) => x.userId,
                  )}
                  firstName={record.firstName}
                  lastName={record.lastName}
                  photoUrl={record.photoUrl}
                  userId={record.userId}
                  onRemove={() => {
                    schedulerRef.current.instance.trigger(
                      'onUserRemovedFromProject',
                      {
                        projectId: record.projectId,
                        userResource: {
                          type: ResourceType.User,
                          user: userLookup[record.userId],
                        },
                      },
                    );
                  }}
                  readOnly={autoAllocation}
                />
              );
            case SchedulerResourceType.Team:
              return (
                <TeamCell
                  taskAssignedTeamIds={allTasksAssignees.teams.map(
                    (x) => x.teamId,
                  )}
                  teamName={record.teamName}
                  teamId={record.teamId}
                  onRemove={() => {
                    schedulerRef.current.instance.trigger(
                      'onTeamRemovedFromProject',
                      {
                        projectId: record.projectId,
                        teamId: record.teamId,
                      },
                    );
                  }}
                  onEdit={() => {
                    onTeamEdit(record.teamId);
                  }}
                  readOnly={autoAllocation}
                />
              );
            case SchedulerResourceType.AssignUserOrPlaceholder:
              size.height = 48;
              cellElement.classList.add('full-width');

              return (
                <AssignUserOrTeam
                  addUsers={onAddUsers}
                  addTeams={onAddTeams}
                  readOnly={autoAllocation}
                />
              );
            default:
              return '';
          }
        },
      },
      {
        text: allocationColumnTitle,
        width: 104,
        id: 'allocatedHours',
        renderer: (arg) => {
          const { record } = arg;
          const { type } = record;

          switch (type as SchedulerResourceType) {
            case SchedulerResourceType.User:
            case SchedulerResourceType.Team:
            case SchedulerResourceType.Project:
              return (
                <TemplateAllocation
                  budgetedHoursRange={record.budgetedHoursRange}
                  totalAllocation={record.totalAllocation ?? 0}
                  isTimesheetFeatureEnabled={isTimesheetFeatureEnabled}
                  showDetailedView={type === SchedulerResourceType.Project}
                />
              );
            case SchedulerResourceType.Role:
            default:
              return '';
          }
        },
      },
    ],
    [
      allTasksAssignees.teams,
      allTasksAssignees.users,
      allocationColumnTitle,
      isTimesheetFeatureEnabled,
      onAddTeams,
      onAddUsers,
      onTeamEdit,
      projectColumnTitle,
      schedulerRef,
      totalAllocationTitle,
      userLookup,
    ],
  );
};
